import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type SortDir = 'asc' | 'desc'

interface SortableHeaderProps<K extends string> {
  label: string
  sortKey: K
  activeKey: K | null | undefined
  dir: SortDir
  onSort: (key: K) => void
  align?: 'left' | 'right'
  className?: string
}

/**
 * Column-header sort button for report tables. Renders inside a `<th>`:
 * the label plus an up/down arrow when this column is active, a faint
 * double arrow otherwise. Pure presentation — the owner keeps `activeKey`
 * and `dir` (in state or the URL) and decides what a click does.
 */
export function SortableHeader<K extends string>({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  className,
}: SortableHeaderProps<K>) {
  const isActive = activeKey === sortKey
  const Icon = !isActive ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onSort(sortKey)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'h-7 -mx-2 px-2 text-xs font-medium uppercase tracking-wide hover:bg-transparent',
        isActive ? 'text-foreground hover:text-foreground' : 'text-muted-foreground hover:text-foreground',
        align === 'right' && 'ml-auto flex-row-reverse',
        className,
      )}
    >
      {label}
      <Icon className={cn('h-3.5 w-3.5', align === 'right' ? 'mr-1.5' : 'ml-1.5', !isActive && 'opacity-50')} />
    </Button>
  )
}

/**
 * Next (key, dir) after clicking `key`: same column flips direction, a new
 * column starts in its natural direction (`defaultDesc` keys newest/largest
 * first, everything else A→Z).
 */
export function nextSort<K extends string>(
  current: { key: K | null | undefined; dir: SortDir },
  key: K,
  defaultDesc: readonly K[] = [],
): { key: K; dir: SortDir } {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  return { key, dir: defaultDesc.includes(key) ? 'desc' : 'asc' }
}
